import { apiRequest } from "@/lib/api";
import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  saveAuthTokens,
} from "@/lib/storage";
import type {
  AuthTokens,
  AuthUser,
  GoogleAuthPayload,
  GoogleAuthResponse,
  LoginPayload,
  LoginResponse,
  RegisterPayload,
  RegisterResponse,
  ResendVerificationResponse,
  VerifyEmailResponse,
} from "@/types/auth";

export async function registerUser(
  payload: RegisterPayload,
): Promise<RegisterResponse> {
  return apiRequest<RegisterResponse>("/auth/register/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function verifyEmail(
  token: string,
): Promise<VerifyEmailResponse> {
  return apiRequest<VerifyEmailResponse>(
    "/auth/verify-email/",
    {
      method: "POST",
      body: JSON.stringify({ token }),
    },
  );
}

export async function resendVerificationEmail(
  email: string,
): Promise<ResendVerificationResponse> {
  return apiRequest<ResendVerificationResponse>(
    "/auth/resend-verification/",
    {
      method: "POST",
      body: JSON.stringify({ email }),
    },
  );
}

export async function loginUser(
  payload: LoginPayload,
): Promise<LoginResponse> {
  const response = await apiRequest<LoginResponse>("/auth/token/", {
    method: "POST",
    body: JSON.stringify(payload),
  });

  if ("two_factor_required" in response) {
    return response;
  }

  saveAuthTokens(response);
  return response;
}

export async function confirmTwoFactorLogin(
  challengeToken: string,
  code: string,
): Promise<AuthTokens> {
  const tokens = await apiRequest<AuthTokens>(
    "/auth/2fa/login/confirm/",
    {
      method: "POST",
      body: JSON.stringify({
        challenge_token: challengeToken,
        code,
      }),
    },
  );

  saveAuthTokens(tokens);
  return tokens;
}

export async function authenticateWithGoogle(
  payload: GoogleAuthPayload,
): Promise<GoogleAuthResponse> {
  const response =
    await apiRequest<GoogleAuthResponse>(
      "/auth/google/",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

  if ("two_factor_required" in response) {
    return response;
  }

  saveAuthTokens({
    access: response.access,
    refresh: response.refresh,
  });

  return response;
}


export async function getCurrentUser(signal?: AbortSignal): Promise<AuthUser> {
  return apiRequest<AuthUser>("/auth/me/", {
    signal,
    method: "GET",
    authenticated: true,
  });
}

export function logoutUser(): void {
  clearAuthTokens();
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken() && getRefreshToken());
}
